import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Globe, UploadSimple, RocketLaunch, FloppyDisk, Image as ImageIcon } from "@phosphor-icons/react";
import { AdminShell } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

const LANGS = ["en", "es", "pt", "fr"];
const LABEL = { en: "EN", es: "ES", pt: "PT", fr: "FR" };
const PAGES = [["landing", "Landing page"], ["features", "Features page"], ["pricing", "Pricing page"], ["distributor", "Distributor page"], ["shell", "Header & Footer"]];
const SEO_PAGES = [["landing", "Landing page"], ["features", "Features page"], ["pricing", "Pricing page"], ["distributor", "Distributor page"]];
const ASSETS = [["logo", "Logo"], ["favicon", "Favicon"], ["og_image", "Share image (OG)"]];

const Counter = ({ len, max }) => (
  <span className={`text-[11px] ${len > max ? "text-red-600 font-semibold" : "text-gray-400"}`}>{len}/{max}</span>
);

export default function AdminWebsite() {
  const [tab, setTab] = useState("brand");
  const [lang, setLang] = useState("en");
  const [brand, setBrand] = useState(null);
  const [page, setPage] = useState("landing");
  const [content, setContent] = useState(null); // {values, english}
  const [seo, setSeo] = useState({});           // {page: {seo, english}}
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => { api.get("/admin/site/brand").then((r) => {
    setBrand(r.data);
    if (r.data.updated_at && (!r.data.published_at || r.data.updated_at > r.data.published_at)) setDirty(true);
  }).catch((e) => toast.error(errDetail(e))); }, []);

  const loadContent = useCallback(() => {
    setContent(null);
    api.get(`/admin/site/content/${page}/${lang}`).then((r) => setContent(r.data)).catch((e) => toast.error(errDetail(e)));
  }, [page, lang]);
  useEffect(() => { if (tab === "content") loadContent(); }, [tab, loadContent]);

  const loadSeo = useCallback(async () => {
    const out = {};
    for (const [p] of SEO_PAGES) {
      const { data } = await api.get(`/admin/site/seo/${p}/${lang}`);
      out[p] = { ...data.english, ...Object.fromEntries(Object.entries(data.seo || {}).filter(([, v]) => typeof v === "string" && v.trim())) , _own: data.seo || {} };
    }
    setSeo(out);
  }, [lang]);
  useEffect(() => { if (tab === "seo") loadSeo().catch((e) => toast.error(errDetail(e))); }, [tab, loadSeo]);

  const saveBrand = async () => {
    try { const { data } = await api.put("/admin/site/brand", { app_name: brand.app_name }); setBrand(data); setDirty(true); toast.success("Brand saved — press Publish to go live"); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind); fd.append("file", file);
    try {
      await api.post("/admin/site/assets", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/admin/site/brand"); setBrand(data); setDirty(true);
      toast.success(`${kind.replace("_", " ")} uploaded — press Publish to go live`);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveContent = async () => {
    try {
      await api.put(`/admin/site/content/${page}/${lang}`, { values: content.values });
      setDirty(true); toast.success("Content saved — press Publish to go live"); loadContent();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const saveSeo = async (p) => {
    const s = seo[p];
    try {
      await api.put(`/admin/site/seo/${p}/${lang}`, {
        meta_title: s.meta_title || "", meta_description: s.meta_description || "", keywords: s.keywords || "",
      });
      setDirty(true); toast.success("SEO saved — press Publish to go live");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/site/publish");
      setDirty(false);
      toast.success(`Published — ${data.generated_files.length} files regenerated`);
    } catch (e) { toast.error(errDetail(e)); }
    setPublishing(false);
  };

  const setVal = (k, v) => setContent((c) => ({ ...c, values: { ...c.values, [k]: v } }));
  const setSeoField = (p, k, v) => setSeo((s) => ({ ...s, [p]: { ...s[p], [k]: v } }));

  return (
    <AdminShell title="Website" subtitle="Brand, marketing content and SEO — regenerates the public HTML pages on Publish." testId="admin-website-page">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex gap-1 border border-gray-200 rounded-md p-1">
          {[["brand", "Brand"], ["content", "Content"], ["seo", "SEO"]].map(([k, l]) => (
            <button key={k} data-testid={`website-tab-${k}`} onClick={() => setTab(k)}
              className={`text-sm font-semibold rounded px-4 py-1.5 ${tab === k ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {tab !== "brand" && (
            <div className="flex gap-1 border border-gray-200 rounded-md p-1" data-testid="website-lang-switch">
              {LANGS.map((l) => (
                <button key={l} data-testid={`website-lang-${l}`} onClick={() => setLang(l)}
                  className={`text-xs font-bold rounded px-3 py-1.5 ${lang === l ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}>{LABEL[l]}</button>
              ))}
            </div>
          )}
          {dirty && <span data-testid="unpublished-badge" className="text-[11px] font-bold uppercase bg-amber-50 text-amber-700 rounded-full px-3 py-1">● Unpublished changes</span>}
          <button data-testid="website-publish-btn" onClick={publish} disabled={publishing}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
            <RocketLaunch size={15} /> {publishing ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      {/* BRAND */}
      {tab === "brand" && brand && (
        <div className="border border-gray-200 rounded-md p-5 max-w-2xl" data-testid="website-brand-panel">
          <div className="flex items-center gap-2 mb-1"><Globe size={18} className="text-blue-600" /><h2 className="font-heading font-bold text-gray-900">Brand</h2></div>
          <p className="text-xs text-gray-500 mb-4">Applies to every language: name in nav, footer, page titles, the WhatsApp mock and structured data.</p>
          <label className="text-xs font-medium text-gray-600 block mb-1">App name</label>
          <input data-testid="brand-appname-input" value={brand.app_name || ""} onChange={(e) => setBrand({ ...brand, app_name: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4" />
          <div className="grid grid-cols-3 gap-4 mb-4">
            {ASSETS.map(([kind, label]) => (
              <div key={kind} className="border border-dashed border-gray-300 rounded-md p-3 text-center">
                <p className="text-xs font-semibold text-gray-600 mb-2">{label}</p>
                {brand[`${kind}_url`] ? (
                  <img src={`${brand[`${kind}_url`].startsWith("/") ? process.env.REACT_APP_BACKEND_URL : ""}${brand[`${kind}_url`]}?v=${brand.updated_at || ""}`}
                    alt={label} className="h-12 mx-auto object-contain mb-2" data-testid={`brand-${kind}-preview`} />
                ) : (
                  <ImageIcon size={28} className="mx-auto text-gray-300 mb-2" />
                )}
                <label className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 cursor-pointer hover:underline">
                  <UploadSimple size={12} /> Upload
                  <input data-testid={`brand-${kind}-upload`} type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadAsset(kind, e.target.files?.[0])} />
                </label>
              </div>
            ))}
          </div>
          <button data-testid="brand-save-btn" onClick={saveBrand}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
            <FloppyDisk size={14} /> Save brand
          </button>
          {brand.published_at && <p className="text-[11px] text-gray-400 mt-3">Last published: {brand.published_at.slice(0, 16).replace("T", " ")} UTC</p>}
        </div>
      )}

      {/* CONTENT */}
      {tab === "content" && (
        <div data-testid="website-content-panel">
          <div className="flex gap-1 mb-4 flex-wrap">
            {PAGES.map(([k, l]) => (
              <button key={k} data-testid={`content-page-${k}`} onClick={() => setPage(k)}
                className={`text-xs font-semibold rounded-full px-4 py-1.5 border ${page === k ? "bg-gray-900 text-white border-gray-900" : "text-gray-600 border-gray-200 hover:bg-gray-50"}`}>{l}</button>
            ))}
          </div>
          {!content ? <p className="text-sm text-gray-400">Loading…</p> : (
            <>
              {lang !== "en" && (
                <p className="text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-3 py-2 mb-4" data-testid="content-fallback-hint">
                  Empty fields fall back to English on the live site. {Object.keys(content.english).filter((k) => !(content.values[k] || "").trim()).length} of {Object.keys(content.english).length} fields still using English.
                </p>
              )}
              <div className="space-y-3 max-w-3xl">
                {Object.keys(content.english).sort().map((k) => {
                  const v = lang === "en" ? (content.values[k] ?? "") : (content.values[k] ?? "");
                  const long = (content.english[k] || "").length > 80;
                  return (
                    <div key={k}>
                      <label className="text-[11px] font-mono font-semibold text-gray-500 block mb-1">{k}</label>
                      {long ? (
                        <textarea data-testid={`content-field-${k}`} rows={2} value={v} placeholder={lang !== "en" ? content.english[k] : ""}
                          onChange={(e) => setVal(k, e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      ) : (
                        <input data-testid={`content-field-${k}`} value={v} placeholder={lang !== "en" ? content.english[k] : ""}
                          onChange={(e) => setVal(k, e.target.value)}
                          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      )}
                    </div>
                  );
                })}
              </div>
              <button data-testid="content-save-btn" onClick={saveContent}
                className="sticky bottom-4 mt-5 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2.5 shadow-lg transition-colors">
                <FloppyDisk size={14} /> Save {PAGES.find(([k]) => k === page)[1]} ({LABEL[lang]})
              </button>
            </>
          )}
        </div>
      )}

      {/* SEO */}
      {tab === "seo" && (
        <div className="space-y-5 max-w-3xl" data-testid="website-seo-panel">
          {SEO_PAGES.map(([p, l]) => {
            const s = seo[p];
            if (!s) return <p key={p} className="text-sm text-gray-400">Loading…</p>;
            return (
              <div key={p} className="border border-gray-200 rounded-md p-5" data-testid={`seo-card-${p}`}>
                <h2 className="font-heading font-bold text-gray-900 mb-3">{l} <span className="text-xs font-normal text-gray-400">· {LABEL[lang]}</span></h2>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Meta title</label>
                  <Counter len={(s.meta_title || "").length} max={60} />
                </div>
                <input data-testid={`seo-title-${p}`} value={s.meta_title || ""} onChange={(e) => setSeoField(p, "meta_title", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">Meta description</label>
                  <Counter len={(s.meta_description || "").length} max={160} />
                </div>
                <textarea data-testid={`seo-desc-${p}`} rows={2} value={s.meta_description || ""} onChange={(e) => setSeoField(p, "meta_description", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <label className="text-xs font-medium text-gray-600 block mb-1">Keywords</label>
                <input data-testid={`seo-keywords-${p}`} value={s.keywords || ""} onChange={(e) => setSeoField(p, "keywords", e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <div className="border border-gray-100 bg-gray-50 rounded-md p-3 mb-4" data-testid={`seo-preview-${p}`}>
                  <p className="text-[11px] text-gray-400 mb-1">Google preview</p>
                  <p className="text-[#1a0dab] text-base leading-snug truncate">{s.meta_title || "Meta title…"}</p>
                  <p className="text-[#006621] text-xs">{`www.yoursite.com/${lang === "en" ? "" : lang + "/"}${p === "landing" ? "" : (p === "distributor" ? "become-a-distributor" : p) + "/"}`}</p>
                  <p className="text-gray-600 text-xs leading-snug line-clamp-2">{s.meta_description || "Meta description…"}</p>
                </div>
                <button data-testid={`seo-save-${p}`} onClick={() => saveSeo(p)}
                  className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
                  <FloppyDisk size={14} /> Save SEO
                </button>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
